import { LoadingOutlined } from "@ant-design/icons"
import { vendorRecommendations } from "@finway-group/shared/lib/consts"
import { PriceIntervalEnum, RightEnum, Vendor } from "@finway-group/shared/lib/models"
import { Button, Empty, Table, Tag, Tooltip } from "antd"
import { ColumnsType, ColumnType } from "antd/lib/table"
import React from "react"
import { AlertTriangle, Plus as PlusIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { NoSearchDataFound } from "~Components/NoSearchDataFound"
import { VendorImage } from "~Components/VendorImage"
import { DataUploadAlert } from "~Components/alert/dataUpload.alert"
import { FilterSectionLegacy } from "~Components/filter/FilterSectionLegacy"
import { ErrorTable } from "~Components/layout/errorTable"
import { Loading } from "~Components/loading"
import { LoadingData } from "~Components/loading.data"
import { PageBody } from "~Components/page/pageBody"
import { PriceLabel } from "~Components/priceLabel"
import { useGroupedExpenseDataForVendors } from "~Shared/hooks/analytics.hooks"
import { useCompany } from "~Shared/hooks/company.hooks"
import { useLoggedInEmployeeProfile } from "~Shared/hooks/user.hooks"
import { RoutePathEnum } from "~Shared/router/RoutePath.enum"
import { AuthzService, VendorService } from "~Shared/services"
import { handleTableConfigChange } from "~Shared/store/actions/tableConfig/tableConfigActions"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { VendorWithExpenseData } from "~Shared/store/reducers/tableReducer"
import { getRecommendationTagColour, insertIf } from "~Shared/utils/helper.utils"
import { useVendorTable } from "../../../shared/hooks/useVendorTable.hooks"
import { getSortOrderFromQueryParams } from "../../../shared/utils/table.utils"
import { VendorTableColumnKeys } from "./vendorTableConsts"

export const VendorTable = ({ onAddVendor }: { onAddVendor: (state: boolean) => void }) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const history = useHistory()
    const { data: vendorTableData, queryParams, isError, refetch, isFetching, isFilterApplied } = useVendorTable()

    const currentEmployee = useLoggedInEmployeeProfile()
    const { globalCurrency } = currentEmployee.settings
    const { isVendorCreationRestricted } = useCompany()

    const { expenseData } = useGroupedExpenseDataForVendors(vendorTableData?.docs ?? [])

    const columns: ColumnsType<VendorWithExpenseData> = [
        {
            title: <span>{t("label:vendor")}</span>,
            key: VendorTableColumnKeys.NAME,
            width: 300,
            className: "pl-10",
            ellipsis: true,
            sortOrder: getSortOrderFromQueryParams(queryParams, VendorTableColumnKeys.NAME),
            sorter: {
                compare: (x: Vendor, y: Vendor) => x.name.localeCompare(y.name),
            },
            render: (vendor: Vendor) => (
                <div className="flex items-center image-loader gap-10">
                    <VendorImage size={"large"} vendor={vendor} />
                    <div className="flex flex-col truncate">
                        <div className="flex items-center mb-3">
                            <p className="font-bold leading-tight mb-0 truncate">{vendor.name}</p>
                            {!vendor?.isApproved && (
                                <Tooltip title={t("info:pending_vendor")}>
                                    <AlertTriangle fill="#FBAC3B" size={18} stroke="#FFFFFF" className="ml-3" />
                                </Tooltip>
                            )}
                        </div>
                        <p className="text-sm text-text leading-none mb-0 truncate">{vendor.description}</p>
                    </div>
                </div>
            ),
        },
        ...insertIf<ColumnType<VendorWithExpenseData>>(AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__ACCESS_TABLE_PAY_READ), {
            title: t("label:creditor_number"),
            key: VendorTableColumnKeys.CREDITOR_NUMBER,
            width: 150,
            sortOrder: getSortOrderFromQueryParams(queryParams, VendorTableColumnKeys.CREDITOR_NUMBER),
            sorter: (vendorA: Vendor, vendorB: Vendor) => vendorA.creditorNumber - vendorB.creditorNumber,
            render: (vendor: Vendor) => vendor.creditorNumber,
        }),
        {
            title: t("label:orders"),
            key: VendorTableColumnKeys.ORDERS,
            width: 100,
            render: (vendor: VendorWithExpenseData) => {
                const vendorExpenseData = expenseData.find((data) => data.id === vendor.id)
                if (!vendorExpenseData) {
                    return <LoadingData />
                }
                return <div>{vendorExpenseData.amount}</div>
            },
        },
        {
            title: t("label:recommendation"),
            key: VendorTableColumnKeys.RECOMMENDATION,
            width: 150,
            sortOrder: getSortOrderFromQueryParams(queryParams, VendorTableColumnKeys.RECOMMENDATION),
            sorter: {
                compare: (a: Vendor, b: Vendor) => a.recommendation - b.recommendation,
            },
            render: (vendor: Vendor) => (
                <div>
                    <Tag className={`min-w-80" ${getRecommendationTagColour(vendorRecommendations[vendor.recommendation])}`}>
                        <span className="">{t(`label:recommendations.${vendorRecommendations[vendor.recommendation].toLowerCase()}`)}</span>
                    </Tag>
                </div>
            ),
        },
        ...insertIf<ColumnType<VendorWithExpenseData>>(AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__TEAM__READ), {
            title: t("label:expenses"),
            key: VendorTableColumnKeys.EXPENSES,
            width: 100,
            align: "right",
            className: "pr-10",
            render: (vendor: VendorWithExpenseData) => {
                const vendorExpenseData = expenseData.find((data) => data.id === vendor.id)
                if (!vendorExpenseData) {
                    return <LoadingData />
                }
                return <PriceLabel value={vendorExpenseData.value || 0} currency={globalCurrency} interval={PriceIntervalEnum.ONE_TIME} />
            },
        }),
    ]

    if (!vendorTableData) {
        return null
    }

    return (
        <div>
            <FilterSectionLegacy table={TablesEnum.VENDORS} />
            {isFetching ? (
                <Loading />
            ) : isError ? (
                <ErrorTable onTableReload={() => refetch()} isLoading={isFetching} />
            ) : vendorTableData.totalDocs === 0 && !isFetching ? (
                !queryParams?.search ? (
                    <>
                        {AuthzService.isDataUploaderGrantedForLoggedInUser() && !isFilterApplied && <DataUploadAlert />}
                        <Empty
                            className="animation-appear"
                            image={`./icons/empty_table.svg`}
                            imageStyle={{
                                height: 150,
                                marginRight: "auto",
                                marginLeft: "auto",
                                marginTop: "15%",
                                marginBottom: "40px",
                                display: "inline-block",
                            }}
                            description={
                                <span className="text-lg font-bold mt-2 mb-0">
                                    {!isFilterApplied ? t("info:empty_state.vendors.title") : t("info:no_filter_data_found.title.vendor")}
                                </span>
                            }
                        >
                            <div className="flex flex-col justify-between items-center">
                                <p>{!isFilterApplied ? t("info:empty_state.vendors.message") : t("info:no_filter_data_found.message")}</p>
                                {VendorService.shouldShowVendorButton(isVendorCreationRestricted) && (
                                    <Button className="mt-14" type="primary" onClick={() => onAddVendor(true)}>
                                        <PlusIcon />
                                        <span>{t("action:vendor:create")}</span>
                                    </Button>
                                )}
                            </div>
                        </Empty>
                    </>
                ) : (
                    <div className="animation-appear">
                        <NoSearchDataFound />
                    </div>
                )
            ) : (
                <div>
                    <PageBody>
                        <Table<Vendor>
                            data-testid="vendorTable"
                            rowKey={(record) => record.id}
                            columns={columns}
                            pagination={{
                                position: ["bottomRight"],
                                showSizeChanger: true,
                                current: queryParams.page,
                                pageSize: queryParams.limit,
                                hideOnSinglePage: false,
                                total: vendorTableData.totalDocs,
                                pageSizeOptions: ["5", "10", "20", "50", "100"],
                            }}
                            onChange={(pagination, _filters, sorter) => {
                                dispatch(handleTableConfigChange(TablesEnum.VENDORS, pagination, sorter))
                            }}
                            dataSource={vendorTableData.docs}
                            loading={{
                                spinning: isFetching,
                                indicator: <LoadingOutlined style={{ fontSize: 30, color: "black" }} spin />,
                            }}
                            onRow={(vendor: Vendor) => ({
                                onClick: () => {
                                    history.push(`${RoutePathEnum.VENDORS}/${vendor.id}`)
                                },
                            })}
                        />
                    </PageBody>
                </div>
            )}
        </div>
    )
}
